import { useMemo, useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CardQuestion, BackButton } from "./CardPrimitives";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import { displaySpecialtyName } from "../specialtyDisplayName";
import type { CardDef } from "../types";
import type { Specialty } from "@/lib/patientIntakeApi";
import type { ProcedureType } from "@/lib/specialtyTags";
import {
  Baby,
  Bone,
  Dna,
  Ear,
  Eye,
  HeartPulse,
  Leaf,
  Loader2,
  Microscope,
  Brain,
  Pill,
  Scissors,
  SmilePlus,
  Stethoscope,
  Syringe,
  Weight,
} from "lucide-react";

interface SpecialtyCardProps {
  card: CardDef;
  value: ProcedureType | string;
  onValue: (v: ProcedureType) => void;
  onNext: () => void;
  onBack?: () => void;
  index: number;
  specialties: Specialty[];
  isLoading: boolean;
  error?: string;
}

/** Lucide icons are stroke-based (outlined). Keep weight light for a refined medical UI. */
const SPEC_ICON_CLASS = "w-7 h-7 sm:w-8 sm:h-8 shrink-0 text-[#166B93]";
const SPEC_ICON_STROKE = 1.65;

function SpecialtyIcon({ slug }: { slug: string }) {
  const p = {
    className: SPEC_ICON_CLASS,
    strokeWidth: SPEC_ICON_STROKE,
    absoluteStrokeWidth: false,
  } as const;

  switch (slug) {
    case "vascular":
      return <HeartPulse {...p} />;
    case "fertility":
      return <Baby {...p} />;
    case "bariatric":
    case "bariatric-surgery":
      return <Weight {...p} />;
    case "wellness":
      return <Leaf {...p} />;
    case "hair-transplant":
    case "hair_transplant":
      return <Scissors {...p} />;
    case "plastic-surgery":
    case "plastic_surgery":
      return <Syringe {...p} />;
    case "diagnostics-imaging":
    case "diagnostics_imaging":
      return <Microscope {...p} />;
    case "dentistry":
      return <SmilePlus {...p} />;
    case "optical":
      return <Eye {...p} />;
    case "stem-cells":
    case "stem_cells":
      return <Dna {...p} />;
    case "wellness-healthcare":
    case "wellness_healthcare":
      return <Pill {...p} />;
    case "orthopedic":
    case "orthopedics":
      return <Bone {...p} />;
    case "neuro-surgery":
      return <Brain {...p} />;
    case "ent":
    case "ear-nose-throat":
    case "ear_nose_throat":
    case "ear-nose-and-throat":
      return <Ear {...p} />;
    case "other":
      return <Stethoscope {...p} />;
    default:
      return <Stethoscope {...p} />;
  }
}

const getSelectedSlug = (value: ProcedureType | string): string => {
  if (typeof value === "string") return value;
  return value?.slug ?? "";
};

export function SpecialtyCard({
  card,
  value,
  onValue,
  onNext,
  onBack,
  index,
  specialties,
  isLoading,
  error,
}: SpecialtyCardProps) {
  const { i18n } = useLingui();
  const [touched, setTouched] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const selectedSlug = getSelectedSlug(value);
  const showError = touched && error;

  const specialtiesWithOther = useMemo(() => {
    if (specialties.some((s) => s.slug === "other")) return specialties;
    return [
      ...specialties,
      {
        id: "specialty-other",
        slug: "other",
        name: i18n._(msg`Other`),
        category: "other",
        description: null,
        display_order: 999,
      },
    ];
  }, [specialties, i18n]);

  useIntakeMobileChromeRegistration(
    () => {
      if (isLoading) return null;
      if (!onBack) return null;
      // Mobile: only Back — choosing a speciality auto-advances (no Continue).
      return {
        onCancel: onBack,
        showContinue: false,
      };
    },
    [isLoading, onBack],
  );

  const handleSelect = (specialty: Specialty) => {
    const isAvailable = specialty.availability?.isAvailable ?? true;
    if (!isAvailable) {
      const expiresAt = specialty.availability?.expiresAt;
      const availableDate = expiresAt
        ? new Date(expiresAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : null;

      setAvailabilityError(
        availableDate
          ? i18n._(
              msg`You already have an active intake for this specialty. You can create a new one after ${availableDate}.`,
            )
          : i18n._(
              msg`You already have an active intake for this specialty and cannot create a new one yet.`,
            ),
      );
      return;
    }

    setAvailabilityError(null);
    const procedure: ProcedureType = {
      id: specialty.id,
      slug: specialty.slug,
      name: specialty.name,
      category: specialty.category,
      description: specialty.description,
      display_order: specialty.display_order,
    };
    onValue(procedure);
    setTimeout(() => onNext(), 280);
  };

  if (isLoading) {
    return (
      <div>
        <CardQuestion question={card.question} hint={card.hint} index={index} />
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">{i18n._(msg`Loading specialties…`)}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      {showError && <p className="mb-4 text-sm text-red-500">{error}</p>}
      {availabilityError && (
        <p className="mb-4 text-sm text-red-500">{availabilityError}</p>
      )}

      <div className="procedure-picker-grid grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-x-1.5 gap-y-1.5 sm:gap-2.5 max-w-2xl [&_button]:!m-0">
        {specialtiesWithOther.map((specialty) => {
          const isSelected = selectedSlug === specialty.slug;
          const isAvailable = specialty.availability?.isAvailable ?? true;
          const availableDate = specialty.availability?.expiresAt
            ? new Date(specialty.availability.expiresAt).toLocaleDateString(
                undefined,
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )
            : null;
          const unavailableLabel = availableDate
            ? i18n._(msg`Until ${availableDate}`)
            : i18n._(msg`Unavailable`);

          return (
            <button
              key={specialty.id}
              type="button"
              disabled={!isAvailable}
              onClick={() => {
                setTouched(true);
                handleSelect(specialty);
              }}
              className={`relative grid h-[102px] sm:h-[118px] grid-rows-[34px_38px] sm:grid-rows-[40px_44px] place-items-center px-2 sm:px-3 py-2 rounded-2xl border-2 text-center transition-all duration-150 ${
                !isAvailable
                  ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed opacity-70"
                  : isSelected
                  ? "border-[#166B93] bg-[#166B93]/8 text-[#166B93] shadow-md shadow-[#166B93]/10"
                  : "border-gray-200 bg-white text-gray-700 hover:border-[#166B93]/40 hover:bg-[#166B93]/4"
              }`}
            >
              <span className="flex h-[34px] sm:h-[40px] items-center justify-center text-[24px] sm:text-3xl leading-none">
                <SpecialtyIcon slug={specialty.slug} />
              </span>
              <span className="flex h-[38px] sm:h-[44px] items-center justify-center text-[13px] sm:text-sm font-semibold leading-[1.2]">
                {displaySpecialtyName(specialty.slug, i18n, specialty.name)}
              </span>
              {!isAvailable && (
                <span className="absolute bottom-1.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-[9px] font-semibold text-gray-600">
                  {unavailableLabel}
                </span>
              )}
              {isSelected && (
                <span className="absolute -top-2 -right-2 bg-[#166B93] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      {onBack && (
        <div className="mt-8 hidden md:block">
          <BackButton onClick={onBack} />
        </div>
      )}
    </div>
  );
}
